import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  listEngagements,
  readAnnotations,
  writeAnnotation,
  terminateEngagement,
} from './grcClient.js';
import { getControlsForFramework, getFrameworkLabel } from './grcFrameworks.js';
import Workbench from '../components/shared/Workbench.jsx';
import Tabs from '../components/shared/Tabs.jsx';

const SCOPE_OPTIONS = [
  { value: '', label: '—' },
  { value: 'in-scope', label: 'In scope' },
  { value: 'out-of-scope', label: 'Out of scope' },
  { value: 'not-applicable', label: 'Not applicable' },
];

const STATUS_COLORS = {
  planning: { bg: 'rgba(148,163,184,0.12)', fg: '#94a3b8', label: 'Planning' },
  active: { bg: 'rgba(99,102,241,0.15)', fg: '#818cf8', label: 'Active' },
  review: { bg: 'rgba(251,191,36,0.10)', fg: '#fbbf24', label: 'Review' },
  'sign-off': { bg: 'rgba(251,191,36,0.15)', fg: '#fbbf24', label: 'Sign-off' },
  concluded: { bg: 'rgba(74,222,128,0.15)', fg: '#4ade80', label: 'Concluded' },
  archived: { bg: 'rgba(148,163,184,0.08)', fg: '#64748b', label: 'Archived' },
  terminated: { bg: 'rgba(251,113,133,0.15)', fg: '#fb7185', label: 'Terminated' },
};

const STATUS_ORDER = [
  'active',
  'review',
  'sign-off',
  'planning',
  'concluded',
  'archived',
  'terminated',
];

function StatusChip({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.planning;
  return (
    <span
      style={{
        fontSize: 10,
        padding: '2px 7px',
        borderRadius: 4,
        background: c.bg,
        color: c.fg,
        fontFamily: 'var(--font-mono)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}
    >
      {c.label}
    </span>
  );
}

function EvidenceBar({ accepted, total }) {
  if (!total) return <span style={{ fontSize: 12, color: 'var(--fg3)' }}>—</span>;
  const pct = Math.round((accepted / total) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 4, borderRadius: 4, background: 'var(--bg3)', minWidth: 60 }}>
        <div
          style={{
            height: '100%',
            borderRadius: 4,
            width: `${pct}%`,
            background: pct === 100 ? '#4ade80' : 'var(--accent)',
            transition: 'width 0.3s',
          }}
        />
      </div>
      <span
        style={{
          fontSize: 11,
          color: 'var(--fg3)',
          whiteSpace: 'nowrap',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {accepted}/{total}
      </span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatPeriod(start, end) {
  const fmt = (d) =>
    d ? new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : null;
  return [fmt(start), fmt(end)].filter(Boolean).join(' – ') || null;
}

// ── Inline annotation editor cell ────────────────────────────────────────────
function ScopeCell({ value, onChange }) {
  return (
    <select
      className="input"
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      onClick={(e) => e.stopPropagation()}
      style={{ fontSize: 12, padding: '2px 6px', width: '100%' }}
    >
      {SCOPE_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function NotesCell({ value, onSave, placeholder = 'Add note…' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  if (!editing) {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        style={{
          fontSize: 12,
          color: value ? 'var(--fg)' : 'var(--fg3)',
          cursor: 'text',
          minHeight: 20,
        }}
      >
        {value || <span style={{ fontStyle: 'italic' }}>{placeholder}</span>}
      </div>
    );
  }

  return (
    <textarea
      className="input"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setEditing(false);
        onSave(draft || null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setEditing(false);
          setDraft(value || '');
        }
      }}
      onClick={(e) => e.stopPropagation()}
      style={{ fontSize: 12, padding: '4px 6px', width: '100%', minHeight: 60, resize: 'vertical' }}
    />
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function GrcClientDetail({ session }) {
  const { clientSlug } = useParams();
  const navigate = useNavigate();

  const clientName = decodeURIComponent(clientSlug || '');

  const [tab, setTab] = useState('engagements');
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name } pending confirmation
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Engagement Workbench rows
  const engagementRows = useMemo(
    () =>
      engagements.map((eng) => ({
        id: eng.id,
        engagementType: eng.engagementType || 'Engagement',
        reference: eng.reference || null,
        status: eng.status || 'planning',
        framework: eng.frameworkId ? getFrameworkLabel(eng.frameworkId) : null,
        frameworkId: eng.frameworkId || null,
        period: formatPeriod(eng.periodStart, eng.periodEnd),
        periodStart: eng.periodStart || null,
        periodEnd: eng.periodEnd || null,
        evidenceAccepted: eng._counts?.accepted ?? 0,
        evidenceTotal: eng._counts?.requests ?? 0,
        createdAt: eng.createdAt || null,
      })),
    [engagements]
  );

  const engagementColumns = useMemo(
    () => [
      {
        id: 'engagementType',
        label: 'Type',
        accessorKey: 'engagementType',
        sortable: true,
        filter: { kind: 'text' },
        size: 220,
        defaultVisible: true,
        cell: ({ row }) => (
          <span style={{ fontWeight: 600, color: 'var(--fg)' }}>{row.original.engagementType}</span>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        accessorKey: 'status',
        sortable: true,
        groupable: true,
        filter: {
          kind: 'select',
          options: STATUS_ORDER.map((s) => ({
            value: s,
            label: STATUS_COLORS[s]?.label || s,
          })),
        },
        size: 120,
        defaultVisible: true,
        cell: ({ row }) => <StatusChip status={row.original.status} />,
      },
      {
        id: 'framework',
        label: 'Framework',
        accessorKey: 'framework',
        sortable: true,
        groupable: true,
        filter: { kind: 'text' },
        size: 200,
        defaultVisible: true,
        cell: ({ row }) => (
          <span
            style={{
              fontSize: 12,
              color: row.original.framework ? 'var(--accent)' : 'var(--fg3)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {row.original.framework || '—'}
          </span>
        ),
      },
      {
        id: 'reference',
        label: 'Reference',
        accessorKey: 'reference',
        sortable: true,
        filter: { kind: 'text' },
        size: 140,
        defaultVisible: true,
        cell: ({ row }) => (
          <span
            style={{
              fontSize: 12,
              color: row.original.reference ? 'var(--fg2)' : 'var(--fg3)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {row.original.reference || '—'}
          </span>
        ),
      },
      {
        id: 'period',
        label: 'Period',
        accessorKey: 'period',
        sortable: false,
        size: 180,
        defaultVisible: true,
        cell: ({ row }) => (
          <span style={{ fontSize: 12, color: row.original.period ? 'var(--fg2)' : 'var(--fg3)' }}>
            {row.original.period || '—'}
          </span>
        ),
      },
      {
        id: 'evidenceTotal',
        label: 'Evidence',
        accessorKey: 'evidenceTotal',
        sortable: true,
        size: 160,
        defaultVisible: true,
        cell: ({ row }) => (
          <EvidenceBar
            accepted={row.original.evidenceAccepted}
            total={row.original.evidenceTotal}
          />
        ),
      },
      {
        id: 'createdAt',
        label: 'Created',
        accessorKey: 'createdAt',
        sortable: true,
        size: 130,
        defaultVisible: true,
        cell: ({ row }) => (
          <span style={{ fontSize: 12, color: 'var(--fg3)' }}>
            {formatDate(row.original.createdAt)}
          </span>
        ),
      },
      {
        id: '_actions',
        label: '',
        accessorKey: 'id',
        sortable: false,
        size: 48,
        defaultVisible: true,
        cell: ({ row }) => (
          <button
            title="Terminate engagement"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget({ id: row.original.id, name: row.original.engagementType });
              setDeleteError('');
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--fg3)',
              fontSize: 15,
              padding: '2px 6px',
              borderRadius: 4,
              lineHeight: 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--danger)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--fg3)';
            }}
          >
            ✕
          </button>
        ),
      },
    ],
    []
  );

  // Annotations keyed by controlRef
  const [annotations, setAnnotations] = useState({});
  const [prevAnnotations, setPrevAnnotations] = useState({});
  const [annLoading, setAnnLoading] = useState(false);

  // The engagement we use for annotations (most recent non-archived)
  const activeEng = useMemo(() => {
    return (
      engagements.find((e) => e.status !== 'archived' && e.status !== 'terminated') ||
      engagements[0] ||
      null
    );
  }, [engagements]);

  // Previous engagement (second most recent)
  const prevEng = useMemo(() => {
    const idx = engagements.indexOf(activeEng);
    return idx >= 0 ? engagements[idx + 1] || null : null;
  }, [engagements, activeEng]);

  const frameworkId = activeEng?.frameworkId || null;
  const controls = useMemo(() => getControlsForFramework(frameworkId), [frameworkId]);

  // ── Load engagements ──────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    listEngagements()
      .then((r) => {
        const all = (r.engagements || []).filter((e) => e.clientName === clientName);
        // Most recent first
        all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setEngagements(all);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [clientName]);

  // ── Load annotations when Controls tab opens ──────────────────────────────
  useEffect(() => {
    if (tab !== 'controls' || !activeEng) return;
    setAnnLoading(true);
    readAnnotations(activeEng.id, prevEng?.id)
      .then((d) => {
        const annMap = {};
        for (const a of d.annotations || []) annMap[a.controlRef] = a;
        setAnnotations(annMap);

        const prevMap = {};
        for (const a of d.prevAnnotations || []) prevMap[a.controlRef] = a;
        setPrevAnnotations(prevMap);
      })
      .catch(() => {
        /* non-fatal */
      })
      .finally(() => setAnnLoading(false));
  }, [tab, activeEng?.id, prevEng?.id]);

  // ── Save annotation field ─────────────────────────────────────────────────
  const saveAnnotation = useCallback(
    async (controlRef, patch) => {
      if (!activeEng || !frameworkId) return;
      const current = annotations[controlRef] || {};
      const next = { ...current, ...patch };

      setAnnotations((prev) => ({ ...prev, [controlRef]: { ...prev[controlRef], ...patch } }));

      try {
        const { annotation: saved } = await writeAnnotation(
          activeEng.id,
          controlRef,
          frameworkId,
          next.scopeStatus ?? null,
          next.notes ?? null,
          next.internalNotes ?? null,
          current.updatedAt ?? null
        );
        // Adopt the server's updatedAt so the next edit's conflict check
        // compares against what actually landed, not this tab's guess.
        if (saved) setAnnotations((prev) => ({ ...prev, [controlRef]: saved }));
      } catch (e) {
        // A stale-version conflict (GH #6094) means another auditor changed
        // this control while we were editing it — show their version rather
        // than either our lost edit or the pre-edit value, so the rollback
        // doesn't look like an uncontested no-op.
        const conflictAnnotation = e?.status === 409 ? e.body?.annotation : null;
        setAnnotations((prev) => ({ ...prev, [controlRef]: conflictAnnotation || current }));
      }
    },
    [activeEng, frameworkId, annotations]
  );

  // ── Workbench rows ────────────────────────────────────────────────────────
  const rows = useMemo(
    () =>
      controls.map((ctrl) => {
        const ann = annotations[ctrl.externalId] || {};
        const prevAnn = prevAnnotations[ctrl.externalId] || {};
        return {
          id: ctrl.externalId,
          ref: ctrl.ref,
          section: ctrl.section,
          title: ctrl.title,
          scopeStatus: ann.scopeStatus || null,
          notes: ann.notes || null,
          internalNotes: ann.internalNotes || null,
          prevScope: prevAnn.scopeStatus || null,
          prevNotes: prevAnn.notes || null,
          updatedAt: ann.updatedAt || null,
        };
      }),
    [controls, annotations, prevAnnotations]
  );

  const SCOPE_LABEL = {
    'in-scope': 'In scope',
    'out-of-scope': 'Out of scope',
    'not-applicable': 'N/A',
  };
  const SCOPE_COLOR = {
    'in-scope': '#4ade80',
    'out-of-scope': '#94a3b8',
    'not-applicable': '#64748b',
  };

  const columns = useMemo(
    () => [
      {
        id: 'ref',
        label: 'Ref',
        accessorKey: 'ref',
        sortable: true,
        size: 100,
        defaultVisible: true,
      },
      {
        id: 'section',
        label: 'Section',
        accessorKey: 'section',
        sortable: true,
        groupable: true,
        filter: {
          kind: 'select',
          options: [...new Set(controls.map((c) => c.section).filter(Boolean))]
            .sort()
            .map((s) => ({ value: s, label: s })),
        },
        size: 100,
        defaultVisible: true,
      },
      {
        id: 'title',
        label: 'Control',
        accessorKey: 'title',
        sortable: true,
        filter: { kind: 'text' },
        size: 300,
        defaultVisible: true,
      },
      {
        id: 'scopeStatus',
        label: 'Scope',
        accessorKey: 'scopeStatus',
        sortable: true,
        groupable: true,
        filter: {
          kind: 'select',
          options: [
            { value: 'in-scope', label: 'In scope' },
            { value: 'out-of-scope', label: 'Out of scope' },
            { value: 'not-applicable', label: 'Not applicable' },
          ],
        },
        size: 140,
        defaultVisible: true,
        cell: ({ row }) => (
          <ScopeCell
            value={row.original.scopeStatus}
            onChange={(v) => saveAnnotation(row.original.id, { scopeStatus: v || null })}
          />
        ),
      },
      {
        id: 'notes',
        label: 'Notes (shared)',
        accessorKey: 'notes',
        sortable: false,
        size: 260,
        defaultVisible: true,
        cell: ({ row }) => (
          <NotesCell
            value={row.original.notes}
            onSave={(v) => saveAnnotation(row.original.id, { notes: v })}
          />
        ),
      },
      {
        id: 'internalNotes',
        label: 'Internal notes 🔒',
        accessorKey: 'internalNotes',
        sortable: false,
        size: 240,
        defaultVisible: true,
        cell: ({ row }) => (
          <NotesCell
            value={row.original.internalNotes}
            placeholder="Internal note…"
            onSave={(v) => saveAnnotation(row.original.id, { internalNotes: v })}
          />
        ),
      },
      {
        id: 'prevScope',
        label: 'Last year',
        accessorKey: 'prevScope',
        sortable: false,
        size: 110,
        defaultVisible: !!prevEng,
        cell: ({ row }) => {
          const v = row.original.prevScope;
          if (!v) return <span style={{ color: 'var(--fg3)', fontSize: 12 }}>—</span>;
          return (
            <span
              style={{
                fontSize: 11,
                color: SCOPE_COLOR[v] || 'var(--fg3)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {SCOPE_LABEL[v] || v}
            </span>
          );
        },
      },
    ],
    [controls, saveAnnotation, prevEng]
  );

  // Engagements are terminated, never deleted (GRC-B-012). The audit tables
  // carry DENY UPDATE, DELETE and all reference grc_engagements, so the row
  // cannot be removed without destroying the trail — which is the point.
  const handleTerminate = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await terminateEngagement(deleteTarget.id, 'Discarded by the firm');
      // It stays in the list, now terminated — it is not removed from view,
      // because pretending it vanished is exactly what the boundary forbids.
      setEngagements((prev) =>
        prev.map((e) => (e.id === deleteTarget.id ? { ...e, status: 'terminated' } : e))
      );
      setDeleteTarget(null);
    } catch (e) {
      setDeleteError(e.message || 'Could not terminate this engagement');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget]);

  if (loading)
    return <div style={{ padding: 40, color: 'var(--fg3)', fontSize: 13 }}>Loading…</div>;
  if (error)
    return <div style={{ padding: 40, color: 'var(--danger)', fontSize: 13 }}>{error}</div>;
  if (!engagements.length) {
    return (
      <div style={{ padding: 40 }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate('/')}
          style={{ marginBottom: 20 }}
        >
          ← Clients
        </button>
        <div style={{ color: 'var(--fg3)', fontSize: 13 }}>
          No engagements found for "{clientName}".
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => navigate('/')}
        style={{ marginBottom: 16 }}
      >
        ← Clients
      </button>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1
          className="display"
          style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg)', marginBottom: 4 }}
        >
          {clientName}
        </h1>
        {frameworkId && (
          <div style={{ fontSize: 12, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
            {getFrameworkLabel(frameworkId)}
          </div>
        )}
        {activeEng?.cvrNumber && (
          <div style={{ fontSize: 12, color: 'var(--fg3)', marginTop: 2 }}>
            CVR {activeEng.cvrNumber}
          </div>
        )}
      </div>

      {/* Tab bar */}
      <Tabs
        ariaLabel="Client sections"
        tabs={['engagements', ...(frameworkId ? ['controls'] : [])].map((t) => ({
          id: t,
          label: t.charAt(0).toUpperCase() + t.slice(1),
          ...(t === 'controls' ? { count: controls.length } : {}),
        }))}
        active={tab}
        onChange={setTab}
        style={{
          marginBottom: 28,
        }}
      />

      {/* Engagements tab */}
      {tab === 'engagements' && (
        <Workbench
          rows={engagementRows}
          columns={engagementColumns}
          storageKey={`grc-engagements-${clientName}`}
          session={session}
          onRowClick={(row) => navigate(`/engagements/${row.original.id}`)}
          enableSelection={false}
          emptyMessage="No engagements found for this client."
          searchPlaceholder="Search engagements…"
        />
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 700,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setDeleteTarget(null);
              setDeleteError('');
            }
          }}
        >
          <div className="card" style={{ width: '100%', maxWidth: 420, padding: 28 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg)', marginBottom: 8 }}>
              Terminate engagement?
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg3)', marginBottom: 16 }}>
              <strong style={{ color: 'var(--fg)' }}>{deleteTarget.name}</strong> will be marked
              terminated and stay in your list. Engagements are never deleted — the audit trail of
              what happened has to survive, which is what an audit firm is holding it for.
            </div>
            {deleteError && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--danger)',
                  marginBottom: 14,
                  padding: '8px 12px',
                  background: 'rgba(251,113,133,0.08)',
                  borderRadius: 6,
                  borderLeft: '3px solid var(--danger)',
                }}
              >
                {deleteError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setDeleteTarget(null);
                  setDeleteError('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm"
                disabled={deleting}
                onClick={handleTerminate}
                style={{ background: 'var(--danger)', color: '#fff', border: 'none' }}
              >
                {deleting ? 'Terminating…' : 'Terminate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Controls tab */}
      {tab === 'controls' && (
        <>
          {annLoading && (
            <div style={{ color: 'var(--fg3)', fontSize: 12, marginBottom: 12 }}>
              Loading annotations…
            </div>
          )}
          {controls.length === 0 ? (
            <div style={{ color: 'var(--fg3)', fontSize: 13 }}>No controls for this framework.</div>
          ) : (
            <Workbench
              rows={rows}
              columns={columns}
              storageKey={`grc-controls-${frameworkId}`}
              session={session}
              enableSelection={false}
              emptyMessage="No controls match your filters."
              searchPlaceholder="Search controls…"
            />
          )}
        </>
      )}
    </div>
  );
}
